import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

/**
 * Generic hook for the dedicated report endpoints (GET /api/<entity>/report).
 * `params` entries with empty/'all' values are skipped; the endpoint returns
 * { summary, results } with all filtering done server-side.
 */
export function useReportApi<TSummary = any>(path: string, params: Record<string, string>) {
  const [summary, setSummary] = useState<TSummary | null>(null);
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const serializedParams = JSON.stringify(params);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const search = new URLSearchParams();
      Object.entries(JSON.parse(serializedParams) as Record<string, string>).forEach(([key, value]) => {
        if (value && value !== 'all') search.set(key, value);
      });
      const qs = search.toString();
      const res: any = await apiService.get(`${path}${qs ? `?${qs}` : ''}`);
      setSummary(res?.summary || null);
      setResults(Array.isArray(res?.results) ? res.results : []);
    } catch (e: any) {
      setError(e?.error?.message || e?.message || 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [path, serializedParams]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return { summary, results, isLoading, error, refetch: fetchReport };
}
